<template>
  <div :class="$style.page">
    <!-- Hero -->
    <section :class="$style.hero">
      <div :class="$style.heroInner">
        <img
          :class="$style.heroLogo"
          src="@/assets/images/linkooklive_longlogo.png"
          alt="링꾹라이브 로고"
        />
        <p :class="$style.heroTag">서브컬처 라이브공연 · 아티스트 플랫폼</p>
        <h1 :class="$style.heroTitle">
          기획부터 운영까지,<br />
          최고의 무대를 위한 파트너.
        </h1>
        <p :class="$style.heroDesc">
          링꾹라이브는 흩어진 공연 정보를 한 곳에 모으고,<br />
          공연을 만드는 분들의 운영을 시스템으로 단단하게 받쳐드려요.
        </p>
        <div :class="$style.heroCtas">
          <button :class="[$style.cta, $style.ctaPrimary]" @click="goTo('/')">
            지금 공연 보러가기
          </button>
          <button :class="[$style.cta, $style.ctaGhost]" @click="openKakao">
            공연·아티스트 문의하기
          </button>
        </div>
      </div>
      <div :class="$style.heroDecor" aria-hidden="true"></div>
    </section>

    <!-- 우리는 누구 -->
    <section :class="$style.section">
      <p :class="$style.eyebrow">ABOUT LINKOOK LIVE</p>
      <h2 :class="$style.sectionTitle">링꾹라이브는요</h2>
      <p :class="$style.sectionLead">
        밴드, 댄스, 일러스트, 코스프레, 인디 페스티벌까지 —<br />
        장르의 경계를 가르지 않는 <b>서브컬처 라이브</b>를 위한 플랫폼이에요.<br />
        관객과 아티스트를 잇는 동시에, 주최자에게는 <b>공연·예매 통합 운영 도구</b>를 제공합니다.
      </p>

      <div :class="$style.statGrid">
        <div :class="$style.statCard">
          <div :class="$style.statValue">언제든</div>
          <div :class="$style.statLabel">공연 정보 확인</div>
          <p :class="$style.statDesc">날짜·장소·아티스트별로 한눈에 정리</p>
        </div>
        <div :class="$style.statCard">
          <div :class="$style.statValue">간편한</div>
          <div :class="$style.statLabel">티켓 예매 · QR 입장</div>
          <p :class="$style.statDesc">예매부터 입장 체크인까지 앱 하나로</p>
        </div>
        <div :class="$style.statCard">
          <div :class="$style.statValue">함께</div>
          <div :class="$style.statLabel">취향이 통하는 커뮤니티</div>
          <p :class="$style.statDesc">관심사 기반 게시판과 공연 후기</p>
        </div>
      </div>
    </section>

    <!-- 누구를 위한 서비스인지 -->
    <section :class="$style.section">
      <p :class="$style.eyebrow">WHO IT'S FOR</p>
      <h2 :class="$style.sectionTitle">두 가지 사용자를 위한 서비스예요</h2>
      <p :class="$style.sectionLead">
        링꾹라이브는 <b>관객</b>과 <b>공연 주최자/아티스트</b>가 만나는 공간이에요.<br />
        아래에서 원하는 영역의 자세한 기능을 확인해보세요.
      </p>

      <div :class="$style.dualNav">
        <a :class="$style.dualNavItem" href="#for-audience" @click.prevent="scrollTo('for-audience')">
          <span :class="$style.dualNavLabel">FOR AUDIENCE</span>
          <span :class="$style.dualNavTitle">관객을 위한 기능</span>
          <span :class="$style.dualNavSub">공연 탐색·예매·QR 입장·커뮤니티</span>
        </a>
        <a :class="$style.dualNavItem" href="#for-organizer" @click.prevent="scrollTo('for-organizer')">
          <span :class="$style.dualNavLabel">FOR ORGANIZER</span>
          <span :class="$style.dualNavTitle">주최자·아티스트 솔루션</span>
          <span :class="$style.dualNavSub">공연관리·예매관리·홍보·현장 운영</span>
        </a>
      </div>
    </section>

    <!-- 관객을 위한 기능 -->
    <section :class="$style.section" id="for-audience">
      <p :class="$style.eyebrow">FOR AUDIENCE</p>
      <h2 :class="$style.sectionTitle">관객, 이런 걸 할 수 있어요</h2>

      <div :class="$style.featureGrid">
        <div
          v-for="feature in audienceFeatures"
          :key="feature.title"
          :class="$style.featureCard"
          @click="goTo(feature.path)"
          :style="{ cursor: feature.path ? 'pointer' : 'default' }"
        >
          <div :class="$style.featureIconWrap">
            <img :src="feature.icon" :alt="feature.title" :class="$style.featureIcon" />
          </div>
          <h3 :class="$style.featureTitle">{{ feature.title }}</h3>
          <p :class="$style.featureDesc">{{ feature.desc }}</p>
          <span v-if="feature.path" :class="$style.featureMore">바로가기 →</span>
        </div>
      </div>
    </section>

    <!-- 주최자 / 아티스트 솔루션 -->
    <section :class="$style.organizerSection" id="for-organizer">
      <div :class="$style.section">
        <p :class="$style.eyebrow">FOR ORGANIZER</p>
        <h2 :class="$style.sectionTitle">주최자·아티스트를 위한 통합 솔루션</h2>
        <p :class="$style.sectionLead">
          <b>“공연 기획자는 콘텐츠에만 집중하세요. 나머지는 링꾹이 관리합니다.”</b><br />
          홍보부터 예매, 현장 운영, 정산까지 — 운영 리소스를 최소화하는 도구를 제공합니다.
        </p>

        <!-- 핵심 3대 영역 -->
        <div :class="$style.pillarGrid">
          <div :class="$style.pillarCard">
            <div :class="$style.pillarTag">MARKETING</div>
            <h3 :class="$style.pillarTitle">SNS 홍보 지원</h3>
            <p :class="$style.pillarDesc">
              공연 매니아들이 모인 링꾹 공식 채널을 통해 잠재 관객에게 가장 정확하게 도달합니다.
            </p>
            <ul :class="$style.pillarList">
              <li><b>공식 계정 포스팅</b> · 인스타그램·트위터 공연 홍보 게시물 등록</li>
              <li><b>타겟 오디언스 노출</b> · 실제 관람객 데이터 기반 맞춤형 바이럴</li>
            </ul>
          </div>

          <div :class="$style.pillarCard">
            <div :class="$style.pillarTag">DATA</div>
            <h3 :class="$style.pillarTitle">예매·정산 데이터 관리</h3>
            <p :class="$style.pillarDesc">
              수기 관리의 휴먼 에러를 시스템으로 완벽히 제거합니다. 투명한 데이터가 정확한 정산의 시작이에요.
            </p>
            <ul :class="$style.pillarList">
              <li><b>실시간 예매 트래킹</b> · 판매 현황을 대시보드로 실시간 확인</li>
              <li><b>자동 정산 리포트</b> · 수기 작업 없는 빠른 정산 프로세스</li>
            </ul>
          </div>

          <div :class="$style.pillarCard">
            <div :class="$style.pillarTag">STAFF</div>
            <h3 :class="$style.pillarTitle">현장 스태프 지원</h3>
            <p :class="$style.pillarDesc">
              현장의 혼란을 방지하는 숙련된 전문 인력을 제공합니다. 무대에만 집중하세요.
            </p>
            <ul :class="$style.pillarList">
              <li><b>입장 및 관객 안내</b> · QR 체크인·입장 팔찌 배부·동선 관리</li>
              <li><b>인력 지원(최대 4명)</b> · 공연 규모에 맞춘 전문 스태프 배치</li>
            </ul>
          </div>
        </div>

        <!-- 예매관리 시스템 상세 -->
        <div :class="$style.subSection">
          <p :class="$style.eyebrowAlt">RESERVATION MANAGEMENT</p>
          <h3 :class="$style.subTitle">예매관리 시스템</h3>
          <p :class="$style.subLead">
            데이터 대조부터 확정 안내까지, 한 화면에서 모든 예매 운영을 끝낼 수 있어요.
          </p>

          <div :class="$style.detailGrid">
            <div :class="$style.detailCard">
              <h4 :class="$style.detailTitle">실시간 예매 현황 관리</h4>
              <ul :class="$style.detailList">
                <li><b>상태별 필터링</b> — 대기중·확정·취소 상태를 탭 하나로 분리해 효율 극대화</li>
                <li><b>스마트 검색</b> — 이름·이메일로 수많은 예매자 데이터 속 정보를 즉시 조회</li>
              </ul>
            </div>
            <div :class="$style.detailCard">
              <h4 :class="$style.detailTitle">예매 상세 데이터 · 처리</h4>
              <ul :class="$style.detailList">
                <li><b>추가 구매 항목 통합</b> — 굿즈·부가 서비스 항목을 별도로 정밀 트래킹</li>
                <li><b>입금 확인 → 자동 발송</b> — 버튼 클릭으로 티켓 번호 발급 + 카카오 알림톡 동시 발송</li>
              </ul>
            </div>
            <div :class="$style.detailCard">
              <h4 :class="$style.detailTitle">개별 예매 인텔리전스</h4>
              <ul :class="$style.detailList">
                <li><b>고유 티켓 식별</b> — 중복 없는 고유 번호로 보안과 관객 신뢰도 확보</li>
                <li><b>신청 이력·타임스탬프</b> — 선착순 분석을 위해 초 단위 신청 일시 기록</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 부가 서비스 -->
        <div :class="$style.subSection">
          <p :class="$style.eyebrowAlt">ADD-ONS</p>
          <h3 :class="$style.subTitle">전문가 부가 서비스</h3>
          <p :class="$style.subLead">
            공연의 품격을 한 단계 높이는 각 분야 전문가의 밀착 지원.
            <span :class="$style.subNote">* 별도 견적, 카카오톡 채널 문의</span>
          </p>

          <div :class="$style.addonGrid">
            <div :class="$style.addonCard">
              <h4 :class="$style.addonTitle">디자인 지원</h4>
              <p :class="$style.addonDesc">
                공연의 정체성을 완성하는 디자인 — 메인 포스터·온·오프라인 홍보물, 티셔츠·키링 등 아티스트 맞춤형 굿즈 시안.
              </p>
            </div>
            <div :class="$style.addonCard">
              <h4 :class="$style.addonTitle">오디오 믹싱</h4>
              <p :class="$style.addonDesc">
                무대 환경에 맞춘 공연용 INST 믹싱과 라이브 사운드 최적화로 아티스트 보컬과 반주의 완벽한 조화를 만듭니다.
              </p>
            </div>
            <div :class="$style.addonCard">
              <h4 :class="$style.addonTitle">전문 인력 연결</h4>
              <p :class="$style.addonDesc">
                매끄러운 진행을 위한 전문 MC 섭외, 고퀄리티 기록을 위한 사진·영상 촬영 작가 파견까지.
              </p>
            </div>
          </div>
        </div>

        <!-- 운영자 문의 CTA -->
        <div :class="$style.organizerCta">
          <div>
            <h3 :class="$style.organizerCtaTitle">공연 운영, 함께 만들어볼까요?</h3>
            <p :class="$style.organizerCtaDesc">
              규모·일정·예산에 맞춘 맞춤 제안을 드립니다. 부담 없이 문의 주세요.
            </p>
          </div>
          <button :class="[$style.cta, $style.ctaPrimary]" @click="openKakao">
            카카오톡으로 상담 신청
          </button>
        </div>
      </div>
    </section>

    <!-- 사용 방법 -->
    <section :class="$style.section">
      <p :class="$style.eyebrow">HOW TO USE</p>
      <h2 :class="$style.sectionTitle">관객은 이렇게 사용하세요</h2>

      <ol :class="$style.steps">
        <li :class="$style.step">
          <span :class="$style.stepNum">1</span>
          <div :class="$style.stepBody">
            <h4 :class="$style.stepTitle">관심 공연을 찾아보세요</h4>
            <p :class="$style.stepDesc">
              메인의 라이브 목록에서 보고 싶은 공연을 살펴보고, 상세 페이지에서
              라인업·일정·장소를 확인하세요.
            </p>
          </div>
        </li>
        <li :class="$style.step">
          <span :class="$style.stepNum">2</span>
          <div :class="$style.stepBody">
            <h4 :class="$style.stepTitle">간편 로그인으로 시작하기</h4>
            <p :class="$style.stepDesc">
              카카오로 3초 만에 로그인하고 찜·예매·커뮤니티 기능을 모두
              사용할 수 있어요.
            </p>
          </div>
        </li>
        <li :class="$style.step">
          <span :class="$style.stepNum">3</span>
          <div :class="$style.stepBody">
            <h4 :class="$style.stepTitle">예매하고 카카오 알림톡으로 확정 안내</h4>
            <p :class="$style.stepDesc">
              입금이 확인되면 고유 티켓 번호가 발급되고 카카오 알림톡으로 자동
              안내됩니다. 마이페이지의 예매내역에서 QR 티켓을 확인할 수 있어요.
            </p>
          </div>
        </li>
      </ol>
    </section>

    <!-- 자주 묻는 질문 -->
    <section :class="$style.section">
      <p :class="$style.eyebrow">FAQ</p>
      <h2 :class="$style.sectionTitle">자주 묻는 질문</h2>

      <div :class="$style.faqList">
        <details
          v-for="(faq, idx) in faqs"
          :key="idx"
          :class="$style.faqItem"
        >
          <summary :class="$style.faqQ">{{ faq.q }}</summary>
          <p :class="$style.faqA" v-html="faq.a"></p>
        </details>
      </div>
    </section>

    <!-- CTA -->
    <section :class="$style.ctaSection">
      <h2 :class="$style.ctaTitle">지금 바로 시작해볼까요?</h2>
      <p :class="$style.ctaDesc">
        관객이라면 가입은 무료, 주최자라면 카카오톡 채널로 부담 없이 문의해주세요.
      </p>
      <div :class="$style.ctaButtons">
        <button :class="[$style.cta, $style.ctaPrimary]" @click="goTo('/')">
          홈으로 가기
        </button>
        <button :class="[$style.cta, $style.ctaGhost]" @click="openKakao">
          카카오톡으로 문의하기
        </button>
      </div>
    </section>

    <!-- 회사 정보 (간략) -->
    <section :class="$style.companyBox">
      <h3 :class="$style.companyTitle">운영사 정보</h3>
      <ul :class="$style.companyList">
        <li><b>상호명</b> 링꾹</li>
        <li><b>대표</b> 이민경</li>
        <li><b>사업자등록번호</b> 897-07-03002</li>
        <li><b>주소</b> 서울특별시 강서구 마곡중앙2로 15, 1층 103호</li>
        <li><b>문의</b> 070-8019-3736</li>
        <li><b>통신판매업 신고번호</b> 제 2026-서울강서-0767 호</li>
      </ul>
      <p :class="$style.companyNotice">
        링꾹라이브는 판매자와 구매자가 만나는 공간을 제공하며,<br />
        실제 상품과 거래에 대한 책임은 판매 회원에게 있습니다.
      </p>
    </section>
  </div>
</template>

<script>
import { setPageMeta } from "@/utils/pageMeta";

export default {
  name: "AboutService",
  data() {
    return {
      audienceFeatures: [
        {
          title: "라이브 공연",
          desc: "공연 일정·장소·라인업을 한눈에 살펴보고 찜으로 저장하세요.",
          icon: require("@/assets/images/family_home.png"),
          path: "/",
        },
        {
          title: "아티스트",
          desc: "팀 소개, 공연 이력, SNS 링크와 영상을 한 페이지에서 만나보세요.",
          icon: require("@/assets/images/family_star.png"),
          path: "/artist",
        },
        {
          title: "공연장",
          desc: "지역별 공연장 정보와 위치, 그동안의 공연 이력까지 확인해요.",
          icon: require("@/assets/images/family_group.png"),
          path: "/venue",
        },
        {
          title: "커뮤니티",
          desc: "공연 후기와 동행 모집, 자유로운 이야기를 나누는 게시판이에요.",
          icon: require("@/assets/images/diversity_3.png"),
          path: "/board",
        },
        {
          title: "동영상",
          desc: "현장의 열기를 그대로 — 아티스트의 공연 영상을 한 곳에서.",
          icon: require("@/assets/images/videos.png"),
          path: "/video",
        },
        {
          title: "QR 티켓 예매",
          desc: "입금 확인 시 카카오 알림톡 + 마이페이지의 QR 티켓으로 입장.",
          icon: require("@/assets/images/stack_star.png"),
          path: "",
        },
      ],
      faqs: [
        {
          q: "링꾹라이브는 어떤 서비스인가요?",
          a: "밴드·댄스·일러스트·코스프레 등 서브컬처 장르의 라이브공연과 아티스트 정보를 한곳에서 찾을 수 있는 플랫폼이에요. 관객에게는 공연 탐색·예매·커뮤니티를, 주최자·아티스트에게는 홍보·예매·현장 운영을 포함한 통합 솔루션을 제공합니다.",
        },
        {
          q: "관객의 이용 요금이 있나요?",
          a: "회원 가입과 기본 기능(공연 탐색, 찜, 커뮤니티 이용 등)은 모두 <b>무료</b>입니다. 공연 티켓 비용은 각 공연 주최자가 정한 가격으로 결제됩니다.",
        },
        {
          q: "공연을 등록하거나 우리 팀(아티스트) 페이지를 만들고 싶어요.",
          a: "카카오톡에서 <b>‘링꾹’ 비즈니스 채널</b>을 검색해 문의해주세요. 공연 등록, 아티스트 페이지 개설, 홍보 배너 안내까지 도와드립니다.",
        },
        {
          q: "공연관리 서비스는 정확히 어떤 것을 도와주나요?",
          a: "① <b>SNS 홍보 지원</b>(인스타그램·트위터 공식 채널 노출), ② <b>실시간 예매 트래킹과 자동 정산 리포트</b>, ③ <b>현장 스태프(최대 4명) 지원</b>(QR 체크인·입장 팔찌 배부 등 동선 관리)이 기본 제공됩니다. 디자인·오디오 믹싱·MC 섭외·촬영은 별도 견적의 부가 서비스로 신청할 수 있어요.",
        },
        {
          q: "예매관리 시스템에서 어떤 기능을 사용할 수 있나요?",
          a: "대기중/확정/취소 <b>상태별 필터링</b>, 이름·이메일 <b>스마트 검색</b>, 굿즈·부가 서비스 <b>추가 구매 항목 통합 관리</b>, 입금 확인 클릭 시 <b>티켓 번호 발급 + 카카오 알림톡 자동 발송</b>, <b>중복 없는 고유 티켓 번호</b>, 선착순 분석을 위한 <b>초 단위 신청 이력</b>까지 한 화면에서 운영할 수 있어요.",
        },
        {
          q: "예매한 티켓은 어디서 확인하나요?",
          a: "로그인 후 우측 상단 프로필 메뉴 → <b>예매내역</b>에서 확인할 수 있어요. 입장 시 QR 코드를 보여주시면 현장 체크인이 가능합니다.",
        },
        {
          q: "서비스 이용 중 문제가 발생했어요.",
          a: "카카오톡 ‘링꾹’ 비즈니스 채널 또는 070-8019-3736으로 연락 주세요. contact@linkook.live 로도 문의하실 수 있습니다.",
        },
      ],
    };
  },
  created() {
    setPageMeta({
      title: "서비스 안내",
      description:
        "링꾹라이브는 서브컬처 라이브공연/아티스트 플랫폼입니다. 관객을 위한 공연 탐색·예매·QR 입장, 주최자·아티스트를 위한 공연관리·예매관리 통합 솔루션을 제공합니다.",
    });
  },
  methods: {
    goTo(path) {
      if (!path) return;
      this.$router.push(path);
    },
    openKakao() {
      window.open("http://pf.kakao.com/_sIxgZX", "_blank");
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.pageYOffset - 20;
      window.scrollTo({ top: y, behavior: "smooth" });
    },
  },
};
</script>

<style module>
/* ================================================
   페이지 컨테이너
   ================================================ */
.page {
  width: 100%;
  margin: 0 auto;
  color: #1f2937;
  font-family: var(--font-nanum-r), sans-serif;
}

/* ================================================
   HERO
   ================================================ */
.hero {
  position: relative;
  width: 100%;
  padding: 56px 24px 64px;
  overflow: hidden;
  background: linear-gradient(160deg, #f0f7ff 0%, #eaf1ff 35%, #f5f0fa 100%);
  border-bottom: 1px solid rgba(102, 189, 255, 0.12);
}

.heroInner {
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}

.heroLogo {
  display: block;
  width: 220px;
  height: auto;
  margin: 0 auto 20px;
}

.heroTag {
  display: inline-block;
  margin: 0 auto 18px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  color: #2a8be0;
  background: rgba(102, 189, 255, 0.15);
  border-radius: 999px;
  letter-spacing: 0.02em;
  font-family: "NanumSquareRoundB", sans-serif;
}

.heroTitle {
  margin: 0 0 18px;
  font-size: 2.2rem;
  line-height: 1.25;
  color: #111827;
  font-family: "Paperlogy8", "NanumSquareRoundEB", sans-serif;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.heroDesc {
  margin: 0 auto 28px;
  font-size: 1rem;
  line-height: 1.7;
  color: #4b5563;
  max-width: 560px;
}

.heroCtas {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta {
  padding: 12px 22px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  font-family: "NanumSquareRoundEB", sans-serif;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease, color 0.15s ease;
  border: 1px solid transparent;
  line-height: 1.2;
}

.cta:hover {
  transform: translateY(-1px);
}

.ctaPrimary {
  background: #66bdff;
  color: #fff;
  box-shadow: 0 6px 18px rgba(102, 189, 255, 0.35);
}

.ctaPrimary:hover {
  background: #4faae9;
  box-shadow: 0 8px 22px rgba(102, 189, 255, 0.45);
}

.ctaGhost {
  background: #ffffff;
  color: #2563eb;
  border-color: #cfe4ff;
}

.ctaGhost:hover {
  background: #f5f9ff;
  border-color: #9ecbff;
}

.heroDecor {
  position: absolute;
  width: 320px;
  height: 320px;
  right: -80px;
  top: -80px;
  background: radial-gradient(closest-side, rgba(102, 189, 255, 0.25), rgba(102, 189, 255, 0));
  border-radius: 50%;
  pointer-events: none;
}

/* ================================================
   섹션 공통
   ================================================ */
.section {
  max-width: 880px;
  margin: 0 auto;
  padding: 64px 24px 16px;
}

.eyebrow {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  color: #66bdff;
  letter-spacing: 0.14em;
  font-family: "NanumSquareRoundEB", sans-serif;
}

.eyebrowAlt {
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  color: #8b6bb8;
  letter-spacing: 0.16em;
  font-family: "NanumSquareRoundEB", sans-serif;
}

.sectionTitle {
  margin: 0 0 14px;
  font-size: 1.7rem;
  color: #111827;
  font-family: "Paperlogy7", "NanumSquareRoundEB", sans-serif;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
}

.sectionLead {
  margin: 0 0 32px;
  font-size: 1rem;
  line-height: 1.7;
  color: #4b5563;
}

/* ================================================
   ABOUT 카드
   ================================================ */
.statGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.statCard {
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  padding: 22px 18px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.statCard:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.statValue {
  font-size: 1.2rem;
  font-weight: 800;
  color: #66bdff;
  font-family: "Paperlogy8", "NanumSquareRoundEB", sans-serif;
  margin-bottom: 4px;
}

.statLabel {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
}

.statDesc {
  margin: 0;
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.5;
}

/* ================================================
   듀얼 네비
   ================================================ */
.dualNav {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.dualNavItem {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 22px 22px;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  color: #111827;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.dualNavItem:hover {
  transform: translateY(-2px);
  border-color: rgba(102, 189, 255, 0.5);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07);
}

.dualNavLabel {
  font-size: 11px;
  letter-spacing: 0.14em;
  color: #66bdff;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 700;
}

.dualNavTitle {
  font-size: 1.05rem;
  color: #111827;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 800;
}

.dualNavSub {
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.5;
}

/* ================================================
   관객 기능 카드
   ================================================ */
.featureGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.featureCard {
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 18px;
  padding: 24px 20px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  display: flex;
  flex-direction: column;
}

.featureCard:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(102, 189, 255, 0.15);
  border-color: rgba(102, 189, 255, 0.4);
}

.featureIconWrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eaf4ff 0%, #f3ecff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.featureIcon {
  width: 28px;
  height: 28px;
  filter: brightness(0) saturate(100%) invert(40%) sepia(99%) saturate(900%)
    hue-rotate(186deg) brightness(101%) contrast(96%);
}

.featureTitle {
  margin: 0 0 6px;
  font-size: 1.05rem;
  color: #111827;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 800;
}

.featureDesc {
  margin: 0 0 12px;
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.6;
  flex: 1;
}

.featureMore {
  font-size: 0.8rem;
  color: #2a8be0;
  font-weight: 700;
  font-family: "NanumSquareRoundEB", sans-serif;
}

/* ================================================
   주최자 솔루션 섹션
   ================================================ */
.organizerSection {
  background: linear-gradient(180deg, #fbfaff 0%, #f5f6fc 100%);
  margin-top: 56px;
  padding-bottom: 40px;
}

.organizerSection .section {
  padding-top: 64px;
}

.pillarGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 8px;
}

.pillarCard {
  background: #ffffff;
  border: 1px solid #ece9f7;
  border-radius: 18px;
  padding: 26px 22px;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pillarCard:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(60, 50, 120, 0.08);
}

.pillarTag {
  display: inline-block;
  padding: 4px 10px;
  margin-bottom: 12px;
  font-size: 10px;
  letter-spacing: 0.14em;
  color: #6b53b8;
  background: rgba(139, 107, 184, 0.12);
  border-radius: 999px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 700;
}

.pillarTitle {
  margin: 0 0 10px;
  font-size: 1.1rem;
  color: #111827;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 800;
}

.pillarDesc {
  margin: 0 0 14px;
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.6;
}

.pillarList {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pillarList li {
  position: relative;
  padding-left: 16px;
  font-size: 0.85rem;
  color: #374151;
  line-height: 1.6;
}

.pillarList li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #8b6bb8;
  font-weight: 700;
}

.pillarList b {
  color: #111827;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 700;
}

/* 서브 섹션 */
.subSection {
  margin-top: 48px;
}

.subTitle {
  margin: 0 0 8px;
  font-size: 1.25rem;
  color: #111827;
  font-family: "Paperlogy7", "NanumSquareRoundEB", sans-serif;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.subLead {
  margin: 0 0 20px;
  font-size: 0.95rem;
  line-height: 1.7;
  color: #4b5563;
}

.subNote {
  display: inline-block;
  margin-left: 6px;
  color: #9ca3af;
  font-size: 0.8rem;
}

.detailGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.detailCard {
  background: #ffffff;
  border: 1px solid #ece9f7;
  border-radius: 14px;
  padding: 20px 18px;
}

.detailTitle {
  margin: 0 0 10px;
  font-size: 0.98rem;
  color: #111827;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 800;
}

.detailList {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detailList li {
  position: relative;
  padding-left: 16px;
  font-size: 0.85rem;
  color: #374151;
  line-height: 1.6;
}

.detailList li::before {
  content: "→";
  position: absolute;
  left: 0;
  color: #66bdff;
  font-weight: 700;
}

.detailList b {
  color: #111827;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 700;
}

/* 부가 서비스 */
.addonGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.addonCard {
  background: linear-gradient(135deg, #ffffff 0%, #faf8ff 100%);
  border: 1px dashed #d4cbe9;
  border-radius: 14px;
  padding: 20px 18px;
}

.addonTitle {
  margin: 0 0 8px;
  font-size: 0.98rem;
  color: #111827;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 800;
}

.addonDesc {
  margin: 0;
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.6;
}

/* 운영자 CTA */
.organizerCta {
  margin-top: 40px;
  padding: 24px 26px;
  background: #ffffff;
  border: 1px solid #ece9f7;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
  box-shadow: 0 8px 20px rgba(60, 50, 120, 0.05);
}

.organizerCtaTitle {
  margin: 0 0 4px;
  font-size: 1.05rem;
  color: #111827;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 800;
}

.organizerCtaDesc {
  margin: 0;
  font-size: 0.88rem;
  color: #6b7280;
  line-height: 1.6;
}

/* ================================================
   스텝
   ================================================ */
.steps {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step {
  position: relative;
  display: flex;
  gap: 18px;
  padding: 20px 22px;
  background: linear-gradient(135deg, #f8fbff 0%, #f9f6ff 100%);
  border: 1px solid #e8eef9;
  border-radius: 14px;
}

.stepNum {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #66bdff;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 800;
  font-family: "Paperlogy7", "NanumSquareRoundEB", sans-serif;
}

.stepBody {
  flex: 1;
}

.stepTitle {
  margin: 4px 0 6px;
  font-size: 1rem;
  color: #111827;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 800;
}

.stepDesc {
  margin: 0;
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.6;
}

/* ================================================
   FAQ
   ================================================ */
.faqList {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.faqItem {
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 4px 4px;
  transition: border-color 0.15s ease;
}

.faqItem[open] {
  border-color: rgba(102, 189, 255, 0.45);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
}

.faqQ {
  list-style: none;
  cursor: pointer;
  padding: 14px 16px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  font-family: "NanumSquareRoundB", sans-serif;
  position: relative;
  padding-right: 40px;
}

.faqQ::-webkit-details-marker {
  display: none;
}

.faqQ::after {
  content: "+";
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #66bdff;
  font-size: 1.4rem;
  font-weight: 700;
  transition: transform 0.2s ease;
}

.faqItem[open] .faqQ::after {
  content: "−";
}

.faqA {
  margin: 0;
  padding: 0 16px 16px;
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.7;
}

/* ================================================
   하단 CTA
   ================================================ */
.ctaSection {
  max-width: 880px;
  margin: 48px auto 0;
  padding: 48px 24px;
  text-align: center;
  background: linear-gradient(135deg, #66bdff 0%, #8aa8ff 60%, #b48aff 100%);
  border-radius: 0;
  color: #ffffff;
}

.ctaTitle {
  margin: 0 0 10px;
  font-size: 1.6rem;
  color: #ffffff;
  font-family: "Paperlogy8", "NanumSquareRoundEB", sans-serif;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.ctaDesc {
  margin: 0 0 24px;
  font-size: 0.95rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.92);
}

.ctaButtons {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.ctaSection .ctaPrimary {
  background: #ffffff;
  color: #2563eb;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.ctaSection .ctaPrimary:hover {
  background: #f6faff;
}

.ctaSection .ctaGhost {
  background: transparent;
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.6);
}

.ctaSection .ctaGhost:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: #ffffff;
}

/* ================================================
   회사 정보 박스
   ================================================ */
.companyBox {
  max-width: 880px;
  margin: 0 auto;
  padding: 40px 24px 24px;
}

.companyTitle {
  margin: 0 0 14px;
  font-size: 0.95rem;
  color: #374151;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 800;
}

.companyList {
  list-style: none;
  padding: 0;
  margin: 0 0 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
  font-size: 0.82rem;
  color: #6b7280;
  line-height: 1.7;
}

.companyList b {
  display: inline-block;
  min-width: 92px;
  color: #374151;
  font-weight: 700;
  font-family: "NanumSquareRoundB", sans-serif;
}

.companyNotice {
  margin: 12px 0 0;
  font-size: 0.78rem;
  color: #9ca3af;
  line-height: 1.6;
}

/* ================================================
   반응형 — 모바일
   ================================================ */
@media screen and (max-width: 768px) {
  .featureGrid,
  .pillarGrid,
  .detailGrid,
  .addonGrid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .statGrid {
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 480px) {
  .hero {
    padding: 36px 18px 48px;
  }

  .heroLogo {
    width: 170px;
  }

  .heroTitle {
    font-size: 1.65rem;
  }

  .heroDesc {
    font-size: 0.9rem;
  }

  .heroCtas .cta {
    flex: 1;
    min-width: 0;
  }

  .section {
    padding: 44px 18px 8px;
  }

  .sectionTitle {
    font-size: 1.35rem;
  }

  .sectionLead {
    font-size: 0.9rem;
    margin-bottom: 22px;
  }

  .dualNav {
    grid-template-columns: 1fr;
  }

  .featureGrid,
  .pillarGrid,
  .detailGrid,
  .addonGrid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .featureCard,
  .pillarCard,
  .detailCard,
  .addonCard {
    padding: 18px 16px;
  }

  .pillarTitle {
    font-size: 1.02rem;
  }

  .subTitle {
    font-size: 1.1rem;
  }

  .step {
    padding: 16px 18px;
    gap: 14px;
  }

  .stepNum {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
  }

  .faqQ {
    font-size: 0.9rem;
    padding: 12px 14px;
    padding-right: 36px;
  }

  .faqA {
    font-size: 0.85rem;
    padding: 0 14px 14px;
  }

  .organizerCta {
    padding: 20px;
    align-items: stretch;
    text-align: left;
  }

  .organizerCta .cta {
    width: 100%;
  }

  .ctaSection {
    margin-top: 36px;
    padding: 36px 20px;
  }

  .ctaTitle {
    font-size: 1.3rem;
  }

  .ctaDesc {
    font-size: 0.88rem;
  }

  .ctaButtons .cta {
    flex: 1;
    min-width: 0;
  }

  .companyBox {
    padding: 32px 18px 18px;
  }

  .companyList {
    grid-template-columns: 1fr;
    font-size: 0.78rem;
  }

  .companyList b {
    min-width: 84px;
  }
}
</style>
